import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const NAVY = '#1B2A4A';
const GOLD = '#C9A84C';
const EMERALD = '#10B981';

type MonthlyPoint = { month: string; revenus: number; tickets: number };
type FillPoint = { event: string; taux: number };
type RevenueTypePoint = { type: string; revenus: number; fill: string };

type OrganizerChartsProps = {
  ticketsMonthly: MonthlyPoint[];
  fillRate: FillPoint[];
  revenueByType: RevenueTypePoint[];
  highlights: Array<{ label: string; value: string; sub: string; icon: string; color: string }>;
};

const formatFCFA = (v: number) =>
  v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`;

const RADIAN = Math.PI / 180;
interface PieLabelProps {
  cx: number; cy: number; midAngle: number; innerRadius: number;
  outerRadius: number; percent: number;
}
const renderLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: PieLabelProps) => {
  if (percent < 0.07) return null;
  const r = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight="bold">{`${(percent * 100).toFixed(0)}%`}</text>;
};

export default function OrganizerCharts({
  ticketsMonthly,
  fillRate,
  revenueByType,
  highlights,
}: OrganizerChartsProps) {
  return (
    <div className="space-y-5">
      {/* Row 1 : Tickets area + Fill rate bar */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {/* Area chart — Tickets mensuels */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center bg-gold/20 rounded-lg">
              <i className="ri-ticket-line text-gold-dark text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy">Tickets & Revenus</h3>
              <p className="text-xs text-gray-400">Évolution sur 7 mois</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={210}>
            <AreaChart data={ticketsMonthly} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="orgRevGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={GOLD} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="orgTickGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={NAVY} stopOpacity={0.2} />
                  <stop offset="95%" stopColor={NAVY} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={formatFCFA} width={38} />
              <Tooltip
                formatter={(value: number, name: string) => [
                  name === 'revenus' ? `${value.toLocaleString('fr-FR')} FCFA` : value,
                  name === 'revenus' ? 'Revenus' : 'Tickets vendus',
                ]}
              />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              <Area type="monotone" dataKey="revenus" stroke={GOLD} strokeWidth={2.5} fill="url(#orgRevGrad)" name="revenus" dot={false} activeDot={{ r: 4 }} />
              <Area type="monotone" dataKey="tickets" stroke={NAVY} strokeWidth={2} fill="url(#orgTickGrad)" name="tickets" dot={false} activeDot={{ r: 4 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Bar chart — Taux de remplissage par événement */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 flex items-center justify-center bg-navy/10 rounded-lg">
              <i className="ri-bar-chart-fill text-navy text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-bold text-navy">Taux de remplissage</h3>
              <p className="text-xs text-gray-400">Par événement (%)</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={210}>
            <BarChart data={fillRate} margin={{ top: 5, right: 10, left: 0, bottom: 0 }} barSize={22}>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" vertical={false} />
              <XAxis dataKey="event" tick={{ fontSize: 10, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} domain={[0, 100]} unit="%" width={35} />
              <Tooltip formatter={(v: number) => [`${v}%`, 'Taux de remplissage']} />
              <Bar dataKey="taux" radius={[6, 6, 0, 0]} name="Taux">
                {fillRate.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={entry.taux >= 80 ? EMERALD : entry.taux >= 50 ? GOLD : NAVY}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Row 2 : Revenus par type + summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        {/* Pie — Revenus par type d'événement */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 flex items-center justify-center bg-gold/20 rounded-lg">
              <i className="ri-pie-chart-2-line text-gold-dark text-xs"></i>
            </div>
            <h3 className="text-sm font-bold text-navy">Revenus par type</h3>
          </div>
          <ResponsiveContainer width="100%" height={160}>
            <PieChart>
              <Pie data={revenueByType} cx="50%" cy="50%" outerRadius={68} innerRadius={28}
                dataKey="revenus" labelLine={false} label={renderLabel}>
                {revenueByType.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip formatter={(v: number) => [`${v.toLocaleString('fr-FR')} FCFA`, 'Revenus']} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {revenueByType.map((s, i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.fill }}></span>
                  <span className="text-xs text-gray-600">{s.type}</span>
                </div>
                <span className="text-xs font-bold text-navy">{formatFCFA(s.revenus)}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Summary highlight cards */}
        <div className="sm:col-span-2 grid grid-cols-2 gap-4">
          {highlights.map((c, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 flex items-start gap-3">
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${c.color} flex-shrink-0`}>
                <i className={`${c.icon} text-white text-base`}></i>
              </div>
              <div>
                <p className="text-xs text-gray-400">{c.label}</p>
                <p className="text-base font-bold text-navy">{c.value}</p>
                <p className="text-xs text-emerald-600 font-medium">{c.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
